import * as React from 'react';
import { ThemeProvider } from '@front10/base-elements';
import { IFrameProvider } from '@front10/utils';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ContextProvider } from '@front10/helpers/dist/Context';

type Direction = 'rtl' | 'ltr';
export type WithContainerProps = {
  styles?: any;
  isIFrame?: boolean;
  translate: (key: string, defaultValue?: string) => string;
  direction: Direction;
  localizationData: any;
  moment?: any;
};

const defaultStyles = {};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 86400000,
      queryFn: () => {}
    }
  }
});

export function withContainer<T>(
  WrappedComponent: React.ComponentType<T>,
  baseStyles: any = defaultStyles
) {
  // Try to create a nice displayName for React Dev Tools.
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithTheme: React.FunctionComponent<T & WithContainerProps> = ({
    styles = defaultStyles,
    ...props
  }: WithContainerProps) => {
    return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={baseStyles}>
          <ThemeProvider theme={styles}>
            <IFrameProvider isIFrame={props.isIFrame}>
              <ContextProvider
                translate={props.translate}
                direction={props.direction}
                localizationData={props.localizationData}
                theme={styles}
                moment={props.moment}
              >
                <WrappedComponent {...(props as T & WithContainerProps)} />
              </ContextProvider>
            </IFrameProvider>
          </ThemeProvider>
        </ThemeProvider>
      </QueryClientProvider>
    );
  };

  ComponentWithTheme.displayName = `withContainer(${displayName})`;

  return ComponentWithTheme;
}

export function withQueryClient<T>(WrappedComponent: React.ComponentType<T>) {
  const ComponentWithQueryClient: React.FunctionComponent<T> = (props) => {
    return (
      <QueryClientProvider client={queryClient}>
        <WrappedComponent {...props} />
        <ReactQueryDevtools />
      </QueryClientProvider>
    );
  };
  return ComponentWithQueryClient;
}
